import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MessageSquare, Send, CheckCircle, Crown, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/components/auth/AuthProvider";
import { useSubscription } from "@/hooks/use-subscription";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Invalid email address").max(255),
  subject: z.string().trim().max(200).optional(),
  message: z.string().trim().min(10, "Message must be at least 10 characters").max(2000),
});

interface ContactFormProps {
  nhSlug: string;
  nursingHomeName: string;
  countySlug?: string;
}

export function ContactForm({ nhSlug, nursingHomeName, countySlug }: ContactFormProps) {
  const { user } = useAuth();
  const { subscribed: isPremium } = useSubscription();
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Pre-fill for logged-in users
  React.useEffect(() => {
    if (user) {
      setForm(prev => ({
        ...prev,
        email: prev.email || user.email || "",
        name: prev.name || user.user_metadata?.first_name 
          ? `${user.user_metadata?.first_name || ""} ${user.user_metadata?.last_name || ""}`.trim()
          : prev.name,
      }));
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast.info("Create an account to message nursing homes", {
        action: { label: "Sign Up", onClick: () => navigate("/auth/signup") }
      });
      return;
    }

    if (!isPremium) {
      toast.info("Upgrade to Premium to send direct messages", {
        action: { label: "Go Premium", onClick: () => navigate("/premium") }
      });
      return;
    }

    setErrors({});
    const result = contactSchema.safeParse(form);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setSending(true);
    try {
      // Insert message into database
      const { error } = await supabase.from("messages").insert({
        nursing_home_id: nhSlug,
        sender_type: "family",
        sender_name: result.data.name,
        sender_email: result.data.email,
        subject: result.data.subject || `Enquiry about ${nursingHomeName}`,
        body: result.data.message,
        sender_user_id: user.id,
      });

      if (error) throw error;

      // Auto-create a lead when a message is sent
      try {
        const { data: existingLead } = await supabase
          .from("nursing_home_leads")
          .select("id")
          .eq("user_id", user.id)
          .eq("nursing_home_id", nhSlug)
          .maybeSingle();

        if (!existingLead) {
          await supabase.from("nursing_home_leads").insert({
            user_id: user.id,
            nursing_home_id: nhSlug,
            nursing_home_name: nursingHomeName,
            location: countySlug || null,
            status: "contacted",
            notes: "Auto-created from direct message",
          });
        }
      } catch (leadErr) {
        console.warn("Failed to auto-create lead:", leadErr);
      }

      // Trigger email notification via edge function
      try {
        await supabase.functions.invoke("notify-new-message", {
          body: {
            nursing_home_id: nhSlug,
            nursing_home_name: nursingHomeName,
            sender_name: result.data.name,
            sender_email: result.data.email,
            subject: result.data.subject || `Enquiry about ${nursingHomeName}`,
            message: result.data.message,
          },
        });
      } catch {
        console.warn("Email notification failed, message still saved");
      }

      setSent(true);
      toast.success("Message sent successfully!");
    } catch (error: any) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Message Sent!</h3>
          <p className="text-muted-foreground">
            Your message has been sent to {nursingHomeName}. They'll receive it via email and can respond directly.
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => {
              setSent(false);
              setForm({ name: "", email: user?.email || "", subject: "", message: "" });
            }}
          >
            Send another message
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Premium gate overlay
  const showGate = !user || !isPremium;

  return (
    <Card className="relative">
      <CardHeader className="pb-3 bg-grandpal-beige/20">
        <CardTitle className="text-lg text-grandpal-green-dark flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Send a Message
          {showGate && <Lock className="h-4 w-4 text-muted-foreground" />}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-5">
        {showGate && (
          <div className="absolute inset-0 z-10 bg-background/80 backdrop-blur-sm flex items-center justify-center rounded-lg">
            <div className="text-center p-6 max-w-xs">
              <Crown className="h-10 w-10 text-yellow-500 mx-auto mb-3" />
              <h4 className="font-semibold text-lg mb-2">Premium Feature</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Send direct messages to nursing homes and get faster responses with Premium.
              </p>
              {!user ? (
                <Button onClick={() => navigate("/auth/signup")} className="w-full bg-grandpal-green-dark">
                  Sign Up Free
                </Button>
              ) : (
                <Button onClick={() => navigate("/premium")} className="w-full bg-gradient-to-r from-yellow-500 to-amber-600 text-white">
                  <Crown className="h-4 w-4 mr-2" />
                  Go Premium
                </Button>
              )}
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="contact-name">Your Name *</Label>
              <Input
                id="contact-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane O'Brien"
                className={errors.name ? "border-destructive" : ""}
                disabled={showGate}
              />
              {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-email">Your Email *</Label>
              <Input
                id="contact-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jane@example.com"
                className={errors.email ? "border-destructive" : ""}
                disabled={showGate}
              />
              {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contact-subject">Subject</Label>
            <Input
              id="contact-subject"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="e.g. Availability for my mother"
              disabled={showGate}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contact-message">Message *</Label>
            <Textarea
              id="contact-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Tell the nursing home about your needs..."
              rows={4}
              className={errors.message ? "border-destructive" : ""}
              disabled={showGate}
            />
            {errors.message && <p className="text-xs text-destructive">{errors.message}</p>}
          </div>
          <Button type="submit" disabled={sending || showGate} className="w-full">
            <Send className="h-4 w-4 mr-2" />
            {sending ? "Sending..." : "Send Message"}
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            Your message will be emailed to {nursingHomeName} directly.
          </p>
        </form>
      </CardContent>
    </Card>
  );
}
